import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { VaBreadcrumbs } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { CONTACTS } from '@department-of-veterans-affairs/component-library/contacts';
import { useFeatureToggle } from '~/platform/utilities/feature-toggles/useFeatureToggle';
import {
  QUICK_SUBMIT_HELP_BC_LABEL,
  QUICK_SUBMIT_HELP_LABEL,
  quickSubmitHelpBC,
  quickSubmitTitle,
} from '../utilities/quickSubmit';
import { SUBMISSIONS_PATH, SUBMIT_FORMS_PATH } from '../utilities/constants';

// This page has no loader, so it is open without sign-in. The app shell
// loads the flags before this page renders.
const QuickSubmitHelpPage = () => {
  const { TOGGLE_NAMES, useToggleValue } = useFeatureToggle();
  const quickSubmitDisabled = !useToggleValue(
    TOGGLE_NAMES.accreditedRepresentativePortalCdUpdateQsLink,
  );
  const isSubmissionsUpdateEnabled = useToggleValue(
    TOGGLE_NAMES.accreditedRepresentativePortalCdUpdateMain,
  );

  useEffect(() => {
    document.title = quickSubmitTitle(QUICK_SUBMIT_HELP_LABEL);
  }, []);

  // The flag is off. Match the other QuickSubmit pages.
  if (quickSubmitDisabled) {
    return (
      <section className="vads-u-width--full">
        <h1 data-testid="qs-help-heading">{QUICK_SUBMIT_HELP_LABEL}</h1>
        <p data-testid="qs-unavailable">This page isn’t available yet.</p>
        <va-link-action
          href={
            isSubmissionsUpdateEnabled ? SUBMIT_FORMS_PATH : SUBMISSIONS_PATH
          }
          text="Go to Submissions page"
          type="primary"
        />
      </section>
    );
  }

  return (
    <>
      <VaBreadcrumbs
        breadcrumbList={quickSubmitHelpBC}
        label={QUICK_SUBMIT_HELP_BC_LABEL}
        homeVeteransAffairs={false}
        wrapping
      />
      <article className="qs-help">
        <h1 data-testid="qs-help-heading">{QUICK_SUBMIT_HELP_LABEL}</h1>

        <div className="vads-l-row">
          <div className="vads-l-col--12 medium-screen:vads-l-col--8">
            <p className="va-introtext">
              This guide explains how to upload forms and files and complete
              eForms using QuickSubmit in the Accredited Representative Portal.
            </p>
            <va-on-this-page />

            <h2 id="getting-there">Opening QuickSubmit in the portal</h2>
            <va-process-list>
              <va-process-list-item header="Go to Submit forms and files">
                <p>
                  From the Accredited Representative Portal{' '}
                  <strong>Dashboard</strong>, select{' '}
                  <strong>Submit forms and files</strong>. The{' '}
                  <strong>Submissions</strong> page opens.
                </p>
              </va-process-list-item>
              <va-process-list-item header="Start an upload">
                <p>
                  Under <strong>Submit forms for your claimants</strong>, select{' '}
                  <strong>Upload with QuickSubmit</strong>. The{' '}
                  <strong>Upload with QuickSubmit</strong> page opens.
                </p>
              </va-process-list-item>
            </va-process-list>

            <h3 id="outside-portal">Opening QuickSubmit outside the portal</h3>
            <p>
              QuickSubmit is also available from{' '}
              <va-link
                href="https://www.va.gov/accessva-tools-and-services/"
                text="AccessVA"
              />
              . Select your user category, then select{' '}
              <strong>QuickSubmit</strong>. You’ll need to sign in with a
              VA-approved sign-in account.
            </p>

            <h2 id="upload">Upload with QuickSubmit</h2>
            <p>
              Your uploads go directly into the Centralized Mail Portal (CM
              Portal) for preliminary processing. Submit completed VA forms and
              supporting documents for a claimant you represent. Attach files
              you already have, or fill out an eForm online and add it to this
              upload.
            </p>

            <va-alert status="warning" visible class="vads-u-margin-y--3">
              <h3 slot="headline" id="before-upload">
                Before you upload
              </h3>
              <ul>
                <li>Submit documents for one Veteran per upload.</li>
                <li>
                  Remove password protection (if applicable) so documents are
                  accessible.
                </li>
                <li>
                  Don’t submit documents with multi-layered properties,
                  regardless of type (Microsoft Word, PDF, or image). These
                  documents will be rejected.
                </li>
              </ul>
              <p>
                <strong>All attached files must meet these criteria:</strong>
              </p>
              <ul>
                <li>A maximum of 30 files can be uploaded.</li>
                <li>
                  Allowed file types: PDF, DOC/DOCX, JPG/JPEG, TIF/TIFF, or PNG.
                </li>
                <li>Recommended resolution is 300 DPI.</li>
                <li>The maximum file size allowed is 200 MB.</li>
                <li data-testid="qs-help-file-name-rule">
                  File names can use only letters, numbers, hyphens,
                  underscores, and periods.
                </li>
              </ul>
              <p className="vads-u-margin-bottom--0">
                If you don’t follow these instructions, you may receive a
                submission rejection notification after the automated submission
                review.
              </p>
            </va-alert>

            <p>
              The <strong>Upload with QuickSubmit</strong> page has three
              sections: <a href="#veteran-info">Veteran’s information</a>,{' '}
              <a href="#submission-info">Submission information</a>, and{' '}
              <a href="#attach-files">Attach files or eForms</a>. Select{' '}
              <strong>Submit</strong> after entering all required information.
            </p>

            <va-process-list>
              <va-process-list-item
                id="veteran-info"
                header="Veteran’s information"
              >
                <p>
                  The <strong>Veteran’s information</strong> section is the
                  first section on the page. Enter the Veteran’s{' '}
                  <strong>First name</strong>, <strong>Middle initial</strong>{' '}
                  (optional), <strong>Last name</strong>,{' '}
                  <strong>Social Security number or VA file number</strong>, and{' '}
                  <strong>Postal code</strong>. If the Veteran lives outside the
                  United States, select{' '}
                  <strong>Veteran lives outside of the United States</strong>.
                  Required fields are marked “(*Required)”.
                </p>
              </va-process-list-item>
              <va-process-list-item
                id="submission-info"
                header="Submission information"
              >
                <p>
                  Select the <strong>Organization</strong> you’re submitting
                  for, then select the <strong>Benefit Claim Type</strong>{' '}
                  (Compensation, Pension, Survivors pension and DIC, or
                  Education) to ensure accurate routing of the documents
                  submitted.
                </p>
                <p>
                  If the documents involve an emergent situation, use the
                  optional <strong>Emergent Indicator</strong> field and select
                  all that apply, so the documents are processed in priority
                  order.
                </p>
              </va-process-list-item>
              <va-process-list-item
                id="attach-files"
                header="Attach files or eForms"
              >
                <p>
                  Attach completed, signed VA forms. If a form asks for
                  supporting documents, such as evidence or additional records,
                  you can attach those here too. You must attach at least one
                  document or eForm to submit. There are two ways to attach
                  files:
                </p>
                <ul>
                  <li>
                    Select <strong>Choose files to upload</strong>, select the
                    document(s) in the window that opens, and then select{' '}
                    <strong>Open</strong>.
                  </li>
                  <li>
                    Drag one or more files from your computer and drop them in
                    the drop area.
                  </li>
                </ul>
              </va-process-list-item>
              <va-process-list-item
                id="attach-eforms"
                header="Fill out an eForm online"
              >
                <p>
                  You can fill out certain VA forms online as eForms. An eForm
                  is the online version of a VA form. It’s the same form with
                  the same information, filled out on screen instead of on
                  paper. eForms provide a quick, electronic submission process
                  that often results in faster, more accurate processing. To
                  open the <a href="#eforms">eForms</a> page, select{' '}
                  <strong>Fill out an eForm online</strong> in the{' '}
                  <strong>Fill out an eForm online</strong> box on the upload
                  page. You can also select{' '}
                  <strong>Browse VA forms library and available eForms</strong>{' '}
                  at the top of the upload page. Both links open the eForms
                  page. Completed eForms you add appear in the{' '}
                  <strong>Attached</strong> list with your files.
                </p>
              </va-process-list-item>
              <va-process-list-item
                id="attached-list"
                header="Review attached items"
              >
                <p>
                  Everything you attach, both files and eForms, appears in the{' '}
                  <strong>Attached</strong> list, which shows how many items are
                  attached. Until you attach something, the list displays “No
                  documents attached yet.”
                </p>
                <p>
                  Each attached file shows its name, file type, and size. Each
                  attached eForm shows the form name and the Veteran it’s for.
                  To remove an item, select <strong>DELETE</strong> below it. To
                  remove everything, select <strong>Clear all</strong>.
                </p>
              </va-process-list-item>
              <va-process-list-item header="Submit">
                <p>
                  Select <strong>Submit</strong> to complete the upload. If any
                  required fields are missing, the page shows which ones you
                  need to complete.
                </p>
                <p>
                  After you select <strong>Submit</strong>, the{' '}
                  <strong>Submission started</strong> page displays. It confirms
                  the Veteran’s name, the upload date, and your confirmation
                  number, and lists the files you uploaded. It also explains
                  what happens next.
                </p>
                <p>
                  Your upload can take up to 10 minutes to appear in your{' '}
                  <a href="#history">submission history</a>. To go there, select{' '}
                  <strong>Submission history</strong>.
                </p>
              </va-process-list-item>
            </va-process-list>

            <h2 id="eforms">eForms</h2>
            <p>
              To fill out VA forms online, select{' '}
              <strong>Fill out an eForm online</strong> on the upload page, or
              select{' '}
              <strong>Browse VA forms library and available eForms</strong> at
              the top of the upload page. Both open the eForms page. The top of
              the eForms page shows the claimant you’re submitting for
              (“Submitting for [claimant name]”).
            </p>
            <p>
              <strong>Drafts</strong> lists your saved eForms. Each draft shows
              its status (<strong>Complete</strong> or{' '}
              <strong>Incomplete</strong>
              ), the Veteran it’s for, and when it was last modified and when it
              expires. Drafts expire 7 days after they were last modified.
              Select <strong>Edit</strong> to continue a draft or{' '}
              <strong>DELETE</strong> to remove it. Incomplete drafts can’t be
              attached. To attach completed drafts, select their checkboxes (or{' '}
              <strong>Select all</strong>
              ), then select <strong>Attach to upload</strong>.
            </p>
            <p>
              <strong>Available eForms and other VA Forms</strong> lists VBA and
              VA Central Office forms. Forms marked <strong>eForm</strong> can
              be completed online. Use <strong>Search forms</strong> (form
              number, title, or usage), the <strong>Benefit category</strong>{' '}
              filter, or the <strong>eForms only</strong> checkbox to narrow the
              list, and <strong>Clear filters</strong> to start over. For each
              form, select <strong>eForm</strong> to complete it online,{' '}
              <strong>PDF</strong> to open the PDF version, or{' '}
              <strong>Details</strong> for more information. Use{' '}
              <strong>List</strong> or <strong>Grid</strong> to change the view.
            </p>
            <p>
              All eForms except the 5103 Notice Response can be started and
              saved as a draft for future editing, or completed and added to
              your upload for submission. You can complete and submit the 5103
              Notice Response form directly in the application.
            </p>
            <p>
              If you select a form and then select{' '}
              <strong>Save and Exit</strong>, it will be listed in{' '}
              <strong>Drafts</strong>. Forms with all required fields completed
              display “Complete” in the <strong>Status</strong> column and may
              be added to an upload for submission. If you delete a draft,
              you’ll be asked to confirm. Deleted drafts are permanently removed
              and can’t be recovered.
            </p>

            <p>
              After you select a form, you can view it as a web form or as a PDF
              by selecting the icons at the top of the page. Select the{' '}
              <strong>Download</strong> icon to download a local copy.
            </p>
            <va-alert status="info" visible class="vads-u-margin-y--3">
              <p className="vads-u-margin-y--0">
                <strong>Note:</strong> Downloads aren’t available for 5103
                Notice Response forms.
              </p>
            </va-alert>
            <ul>
              <li>
                To move through a form, select <strong>Next</strong> or{' '}
                <strong>Previous</strong>. <strong>Next</strong> won’t move to
                the next page if required data is missing or doesn’t match
                validation rules. Use the sidebar menu to move around without
                applying validation rules.
              </li>
              <li>
                Select <strong>Save and Exit</strong> to save the form and
                return to the <strong>eForms</strong> page. The saved form
                displays in <strong>Drafts</strong> with “Incomplete” in the{' '}
                <strong>Status</strong> column.
              </li>
              <li>
                Select <strong>Attach to Upload</strong> to attach the selected
                form to the current upload and go to the{' '}
                <strong>Upload with QuickSubmit</strong> page.
              </li>
            </ul>

            <h2 id="history">Submission history</h2>
            <p>
              To see your recent submissions, select{' '}
              <strong>Submission history</strong> in the side navigation on the
              Submissions page. The list shows all Online Form submissions that
              went through the portal for the claimants you represent and any
              QuickSubmit uploads submitted by the current user from the past 60
              days.
            </p>

            <h2 id="emails">QuickSubmit email notifications</h2>
            <p>
              You’ll receive email notifications for successful submission or
              rejection of a packet.
            </p>

            <h3 id="confirmation-email">Successful submission confirmation</h3>
            <p>
              A successful submission email includes the Confirmation Number.
            </p>
            <p>
              Select the <strong>QuickSubmit submission</strong> link in the
              email to view the upload.
            </p>
            <p>
              To change the email address where these notifications are sent,
              sign in to QuickSubmit directly through AccessVA. You can’t change
              it in the portal. For how to open QuickSubmit outside the portal,
              see{' '}
              <a href="#outside-portal">
                Opening QuickSubmit outside the portal
              </a>
              .
            </p>

            <h3 id="rejected">Submission rejection</h3>
            <p>
              A submission rejection notification means the upload was denied by
              the automated review process. Submissions are rejected for reasons
              including:
            </p>
            <ul>
              <li>Missing documents</li>
              <li>
                Attached files that don’t meet the specified file criteria
              </li>
              <li>
                Inaccessible files (for example, file corruption or password
                protection)
              </li>
              <li>
                Files (PDF, JPG/JPEG, TIF/TIFF, or DOC/DOCX) with multi-layered
                properties
              </li>
              <li>Multiple Veterans in one submission</li>
              <li>
                Security risks (for example, malware such as a virus, worm,
                Trojan, or hybrid)
              </li>
              <li>Technical difficulties</li>
            </ul>
            <p>
              To resubmit, perform the upload process again. Make sure your
              upload follows the rules in{' '}
              <a href="#before-upload">Before you upload</a>, and resubmit all
              required documents. Confirm there are no missing documents and
              that all files meet the <a href="#before-upload">file criteria</a>
              .
            </p>

            <p className="vads-u-margin-top--5 vads-u-color--gray-medium">
              Last updated: October 23, 2026
            </p>
          </div>
          <div className="vads-l-col--12 medium-screen:vads-l-col--4 qs-help__contact">
            <va-accordion open-single>
              <va-accordion-item
                header="Need more help?"
                id="need-help"
                level="2"
                open="open"
              >
                <h3 className="vads-u-margin-top--0 vads-u-font-size--h4">
                  Accredited Representative Portal
                </h3>
                <p>
                  For help using the portal, see{' '}
                  <Link to="/help">
                    Get help with the Accredited Representative Portal
                  </Link>
                  .
                </p>
                <h3 className="vads-u-font-size--h4">
                  Technical support for uploads and eForms
                </h3>
                <p>
                  For problems uploading files or completing eForms, email{' '}
                  <a href="mailto:quick-submit-helpdesk@mms-govcio.com">
                    quick-submit-helpdesk@mms-govcio.com
                  </a>
                  .
                </p>
                <h3 className="vads-u-font-size--h4">
                  VA benefits and claim status
                </h3>
                <p className="vads-u-margin-bottom--0">
                  For questions about VA benefits or claim status, call{' '}
                  <va-telephone contact={CONTACTS.VA_BENEFITS} /> or visit{' '}
                  <va-link href="https://www.va.gov/" text="VA.gov" />.
                </p>
              </va-accordion-item>
            </va-accordion>
          </div>
        </div>
      </article>
    </>
  );
};

export default QuickSubmitHelpPage;
